'use client';

import { useTranslations } from 'next-intl';

import { ButtonLink } from '@commons/components/Button';
import PageBuilder from '@commons/pagebuilder';
import GatedInsightDrawer from '@commons/pagebuilder/webforms/GatedInsightDrawer';
import type { DrupalBlock, DrupalDocument } from '@commons/types/drupal';

import type { InsightNodeProps } from '.';

// Type definition for the InsightDownloadButton component properties.

type Props = {
  file: DrupalDocument;
  isGated?: boolean;
};

/**
 * InsightDownloadButton component.
 *
 * Renders a button to download a file or a gated form to download a file.
 *
 * Also used in the InsightMetadata plugin block.
 */

export const InsightDownloadButton = ({ file, isGated }: Props) => {
  const t = useTranslations(`forms.gatedInsight`);

  const fileURL = file?.url || '#';

  if (!isGated) {
    return (
      <ButtonLink variant="primary" href={fileURL}>
        {t('trigger')}
      </ButtonLink>
    );
  }

  return (
    <GatedInsightDrawer
      url={fileURL}
      syncFormQueryParam
      triggerProps={{ label: t('trigger'), variant: 'primary' }}
    />
  );
};

/**
 * InsightDownloadButton field block component.
 */

export const InsightDownloadButtonBlock = ({ content }: DrupalBlock<DrupalDocument>) => {
  if (!content?.url) return null;

  const page = PageBuilder.getPage<InsightNodeProps>();

  const isGated = page?.isGated;

  return <InsightDownloadButton file={content} isGated={isGated} />;
};

export default InsightDownloadButtonBlock;
